import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, PlayCircle } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const FILTERS = [
  { key: "all", label: "All" },
  { key: "engineering", label: "Engineering" },
  { key: "trading", label: "Trading" },
  { key: "school", label: "School 1–12" },
  { key: "mba", label: "MBA Finance" },
];

const SUBS = {
  engineering: [
    ["all", "All Branches"], ["common", "Common"], ["mechanical", "Mechanical"], ["diploma", "Mech Diploma"], ["civil", "Civil"],
    ["computer", "Computer / IT"], ["electrical", "Electrical"], ["ec", "E & C"], ["chemical", "Chemical"],
  ],
  school: [["all", "All Boards"], ["gseb", "GSEB"], ["cbse", "CBSE"], ["icse", "ICSE"]],
};

const CAT_LABEL = { engineering: "Engineering", trading: "Trading", school: "School", mba: "MBA Finance" };
const BRANCH_LABEL = { common: "All Branches", mechanical: "Mechanical", diploma: "Mech Diploma", civil: "Civil", computer: "Computer/IT", electrical: "Electrical", ec: "E & C", chemical: "Chemical" };
const BOARD_LABEL = { gseb: "GSEB", cbse: "CBSE", icse: "ICSE" };

export const Courses = ({ courses, filter, setFilter }) => {
  const [sub, setSub] = useState("all");
  const pickFilter = (f) => {
    setFilter(f);
    setSub("all");
  };
  const subChips = SUBS[filter] || null;
  const visible = courses.filter(
    (c) => (filter === "all" || c.category === filter) && (sub === "all" || c.branch === sub || c.board === sub)
  );

  return (
    <section id="courses" className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32" data-testid="courses-section">
      <div className="mx-auto max-w-[1600px]">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-8">
          <div>
            <p className="mb-4 text-xs font-medium uppercase tracking-[0.4em] text-brand">Catalogue</p>
            <h2 className="font-display text-4xl font-black uppercase leading-none tracking-tighter sm:text-5xl lg:text-6xl">
              The Courses<span className="text-brand">.</span>
            </h2>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="course-filters">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={`filter-tab-${f.key}`}
                onClick={() => pickFilter(f.key)}
                className={`border px-5 py-2.5 text-xs font-bold uppercase tracking-widest transition-colors duration-300 ${
                  filter === f.key ? "border-brand bg-brand text-white" : "border-white/15 text-zinc-400 hover:border-white/40 hover:text-white"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        {subChips && (
          <div className="mb-12 flex flex-wrap gap-2 border-b border-white/10 pb-8" data-testid="sub-filters">
            {subChips.map(([k, label]) => (
              <button
                key={k}
                data-testid={`sub-filter-${k}`}
                onClick={() => setSub(k)}
                className={`px-4 py-2 text-xs font-medium uppercase tracking-widest transition-colors duration-300 ${
                  sub === k ? "text-brand" : "text-zinc-500 hover:text-white"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
        <p className="mb-8 text-xs uppercase tracking-widest text-zinc-600" data-testid="course-count">{visible.length} courses</p>
        <div className="grid grid-cols-1 gap-x-8 gap-y-16 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((course, i) => (
            <motion.div
              key={course.slug}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: (i % 3) * 0.08, duration: 0.6, ease: EASE }}
              layout
            >
              <Link to={`/course/${course.slug}`} data-testid={`course-card-${course.slug}`} className="group block">
                <div className="overflow-hidden border border-white/10">
                  <img
                    src={course.image}
                    alt={course.title}
                    loading="lazy"
                    className="aspect-[16/10] w-full object-cover opacity-80 transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
                <div className="mt-5 flex items-start justify-between gap-4">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-brand">
                      {CAT_LABEL[course.category]}
                      {course.bundle ? " · Bundle" : ""}
                      {course.branch ? ` · ${BRANCH_LABEL[course.branch] || course.branch}` : ""}
                      {course.board ? ` · ${BOARD_LABEL[course.board] || course.board}` : ""}
                    </div>
                    <h3 className="mt-2 font-display text-xl font-bold leading-tight tracking-tight md:text-2xl">{course.title}</h3>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="font-display text-xl font-black tracking-tight">₹{course.price.toLocaleString("en-IN")}</div>
                    <div className="text-xs text-zinc-600 line-through">₹{course.mrp.toLocaleString("en-IN")}</div>
                  </div>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-zinc-500">{course.tagline}</p>
                <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-4">
                  <div className="flex items-center gap-4 text-xs text-zinc-500">
                    <span className="flex items-center gap-1.5"><PlayCircle size={13} /> {course.bundle ? `${course.bundle_courses?.length || 0} subjects` : `${course.lessons.length} chapters`}</span>
                    <span className="flex items-center gap-1.5"><Clock size={13} /> {course.level}</span>
                  </div>
                  <ArrowRight size={16} className="text-zinc-600 transition-all duration-300 group-hover:translate-x-1 group-hover:text-brand" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
